import i18n from 'modules/core/lib/i18n'

import {
  EdgeUI,
  NodeUI,
  NodeUIApproval,
  NodeUICondition,
  NodeUITask,
} from 'modules/workflows/components/Node/types'
import { isNodeUIConditionValid } from 'modules/workflows/components/Node/utils'

// Validation error types
export type WorkflowValidationErrorType =
  | 'INCOMPLETE_BLOCK'
  | 'INVALID_BRANCH_TERMINATION'
  | 'DISCONNECTED_NODE'

export type WorkflowValidationError = {
  type: WorkflowValidationErrorType
  nodeId: string
  message: string
}

export type WorkflowValidationResult = {
  isValid: boolean
  errors: WorkflowValidationError[]
}

// Type guards
const isTaskOrApprovalNode = (node: NodeUI): node is NodeUITask | NodeUIApproval => {
  return node.type === 'task' || node.type === 'approvals'
}

const isConditionNode = (node: NodeUI): node is NodeUICondition => {
  return node.type === 'condition'
}

const isConditionBranchNode = (node: NodeUI): boolean => {
  return node.type === 'conditionIf' || node.type === 'conditionElse'
}

const isTriggerNode = (node: NodeUI): boolean => {
  return node.type === 'trigger'
}

/**
 * Checks if a task or approval node is complete (has handler_name set)
 */
export const isActionNodeComplete = (node: NodeUITask | NodeUIApproval): boolean => {
  return !!node.data.handler_name && node.data.handler_name.trim() !== ''
}

/**
 * Gets the validation error message for a specific node
 */
export const getNodeValidationError = (node: NodeUI): string | null => {
  if (isTaskOrApprovalNode(node)) {
    if (!isActionNodeComplete(node)) {
      return i18n.t('workflows.validation.incomplete_action_block')
    }
  }

  if (isConditionNode(node)) {
    if (!isNodeUIConditionValid(node)) {
      return i18n.t('workflows.validation.incomplete_condition_block')
    }
  }

  return null
}

/**
 * Checks if a node is complete based on its type
 */
export const isNodeComplete = (node: NodeUI): boolean => {
  // Trigger nodes are always complete
  if (isTriggerNode(node)) {
    return true
  }

  // Condition branch nodes (conditionIf, conditionElse) are UI-only and always valid
  if (isConditionBranchNode(node)) {
    return true
  }

  // Task and approval nodes need handler_name
  if (isTaskOrApprovalNode(node)) {
    return isActionNodeComplete(node)
  }

  // Condition nodes need valid comparisons
  if (isConditionNode(node)) {
    return isNodeUIConditionValid(node)
  }

  return true
}

/**
 * Finds all terminal nodes (nodes with no outgoing edges)
 * Excludes condition branch nodes since they are UI-only constructs
 */
export const findTerminalNodes = (nodes: NodeUI[], edges: EdgeUI[]): NodeUI[] => {
  const nodesWithOutgoingEdges = new Set(edges.map((edge) => edge.source))

  return nodes.filter((node) => {
    // Skip condition branch nodes - they're UI-only
    if (isConditionBranchNode(node)) {
      return false
    }

    // Skip condition nodes - they always have outgoing edges to their branches
    if (isConditionNode(node)) {
      return false
    }

    return !nodesWithOutgoingEdges.has(node.id)
  })
}

/**
 * Finds nodes that end each branch of a condition node
 * by following edges from conditionIf and conditionElse nodes
 */
const findBranchTerminalNodes = (
  conditionNode: NodeUICondition,
  nodes: NodeUI[],
  edges: EdgeUI[]
): NodeUI[] => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))

  // Find the condition branch nodes (conditionIf, conditionElse) that belong to this condition
  const branchNodes = nodes.filter((n) => {
    if (!isConditionBranchNode(n)) return false
    // Check if there's an edge from the condition node to this branch node
    return edges.some((e) => e.source === conditionNode.id && e.target === n.id)
  })

  const terminalNodes: NodeUI[] = []

  // For each branch, follow the edges to find where it terminates
  for (const branchNode of branchNodes) {
    const outgoingEdges = edges.filter((e) => e.source === branchNode.id)

    if (outgoingEdges.length === 0) {
      // Branch has no outgoing edge - it terminates at the branch node itself
      terminalNodes.push(branchNode)
    } else {
      // Follow the edges to find actual terminal nodes
      for (const edge of outgoingEdges) {
        const targetNode = nodeMap.get(edge.target)
        if (targetNode) {
          // We found a node after the branch - check if this is terminal
          const furtherEdges = edges.filter((e) => e.source === targetNode.id)
          if (furtherEdges.length === 0 && !isConditionNode(targetNode)) {
            terminalNodes.push(targetNode)
          }
        }
      }
    }
  }

  return terminalNodes
}

/**
 * Validates that all branches terminate in Task or Approval blocks
 */
export const validateBranchTermination = (
  nodes: NodeUI[],
  edges: EdgeUI[]
): WorkflowValidationError[] => {
  const errors: WorkflowValidationError[] = []

  // Find all terminal nodes in the workflow
  const terminalNodes = findTerminalNodes(nodes, edges)
  // Check each terminal node - it must be a Task or Approval
  for (const node of terminalNodes) {
    if (!isTaskOrApprovalNode(node)) {
      // Skip trigger nodes - they're entry points, not termination points
      if (isTriggerNode(node)) continue

      errors.push({
        type: 'INVALID_BRANCH_TERMINATION',
        nodeId: node.id,
        message: i18n.t('workflows.validation.invalid_branch_termination'),
      })
    }
  }

  // Also check condition node branches specifically
  const conditionNodes = nodes.filter(isConditionNode)

  for (const conditionNode of conditionNodes) {
    const branchTerminals = findBranchTerminalNodes(conditionNode, nodes, edges)

    for (const terminal of branchTerminals) {
      // If a branch terminates at a conditionIf or conditionElse, it means no block was added after
      if (isConditionBranchNode(terminal)) {
        errors.push({
          type: 'INVALID_BRANCH_TERMINATION',
          nodeId: terminal.id,
          message: i18n.t('workflows.validation.empty_branch'),
        })
      }
    }
  }

  return errors
}

/**
 * Validates that all blocks are complete
 */
export const validateBlockCompleteness = (nodes: NodeUI[]): WorkflowValidationError[] => {
  const errors: WorkflowValidationError[] = []

  for (const node of nodes) {
    // Skip condition branch nodes
    if (isConditionBranchNode(node)) continue

    if (!isNodeComplete(node)) {
      const errorMessage = getNodeValidationError(node)
      errors.push({
        type: 'INCOMPLETE_BLOCK',
        nodeId: node.id,
        message: errorMessage || i18n.t('workflows.validation.incomplete_block'),
      })
    }
  }

  return errors
}

/**
 * Main validation function for the entire workflow
 */
export const validateWorkflow = (nodes: NodeUI[], edges: EdgeUI[]): WorkflowValidationResult => {
  const errors: WorkflowValidationError[] = []

  // Validate block completeness
  errors.push(...validateBlockCompleteness(nodes))

  // Validate branch termination
  errors.push(...validateBranchTermination(nodes, edges))

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Gets a summary of validation errors for display
 */
export const getValidationErrorSummary = (errors: WorkflowValidationError[]): string => {
  if (errors.length === 0) {
    return ''
  }

  const incompleteBlocks = errors.filter((e) => e.type === 'INCOMPLETE_BLOCK')
  const invalidTerminations = errors.filter((e) => e.type === 'INVALID_BRANCH_TERMINATION')

  const parts: string[] = []

  if (incompleteBlocks.length > 0) {
    parts.push(
      i18n.t('workflows.validation.summary.incomplete_blocks', { count: incompleteBlocks.length })
    )
  }

  if (invalidTerminations.length > 0) {
    parts.push(
      i18n.t('workflows.validation.summary.invalid_terminations', {
        count: invalidTerminations.length,
      })
    )
  }

  return parts.join('. ')
}
